import { useState, useMemo } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Upload, CheckCircle, AlertCircle, ArrowLeft, Bell, Loader2 } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import { useCampaignDetail } from '@/hooks/useCampaignDetail'

const UploadFinalVideo = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const { campaign, isLoading: detailLoading } = useCampaignDetail(id)
  // Find the milestones
  const videoReviewMilestone = useMemo(() => {
    if (!campaign?.milestones) return null
    return campaign.milestones.find(
      (m) =>
        m.name.toLowerCase().includes('video for review') ||
        m.name.toLowerCase().includes('upload video for review'),
    )
  }, [campaign?.milestones])

  const finalVideoMilestone = useMemo(() => {
    if (!campaign?.milestones) return null
    return campaign.milestones.find(
      (m) =>
        m.name.toLowerCase().includes('final video') ||
        m.name.toLowerCase().includes('upload final video'),
    )
  }, [campaign?.milestones])

  // Check if video review is approved
  const videoReviewSubmission = useMemo(() => {
    if (!videoReviewMilestone?.submissions) return null
    return videoReviewMilestone.submissions.sort((a: any, b: any) => b.version - a.version)[0]
  }, [videoReviewMilestone?.submissions])

  const isVideoReviewApproved = videoReviewSubmission?.status === 'approved'

  // Check if there's a final video submission
  const activeFinalSubmission = useMemo(() => {
    if (!finalVideoMilestone?.submissions) return null
    return finalVideoMilestone.submissions.find(
      (sub: any) => sub.status === 'pending_review' || sub.status === 'approved',
    )
  }, [finalVideoMilestone?.submissions])

  const isFinalVideoApproved = useMemo(() => {
    return activeFinalSubmission?.status === 'approved'
  }, [activeFinalSubmission])

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  // Show loading state while fetching campaign
  if (detailLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#C0FD72]" />
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold mb-4 text-white">Campaign Not Found</h2>
        <p className="mb-4 text-gray-300">The campaign you're looking for doesn't exist.</p>
        <Link to="/" className="text-blue-500 hover:underline">
          Return to Dashboard
        </Link>
      </div>
    )
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('No file selected', { description: 'Please select a video file to upload' })
      return
    }

    setUploading(true)

    // TODO: Implement actual upload using uploadVideoForMilestone
    setTimeout(() => {
      setUploading(false)
      toast('Upload Not Yet Implemented', {
        description: 'Final video upload will be implemented in the next phase',
      })
    }, 1000)
  }

  const getStatusData = () => {
    if (!activeFinalSubmission) return null

    switch (activeFinalSubmission.status) {
      case 'uploaded':
        return {
          text: 'Final video uploaded successfully. Ready to post to your selected platforms.',
          cta: null,
        }
      case 'posted':
        return {
          text: 'Successfully posted to channel.',
          cta: null,
        }
      default:
        return null
    }
  }

  const statusData = getStatusData()

  return (
    <div>
      <Link
        to={`/collaborations/${campaign.id}`}
        className="text-gray-300 hover:text-white inline-flex items-center gap-2 text-sm mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Collaboration Details
      </Link>

      {statusData && (
        <div className="mb-4 bg-[#F4DB66] rounded-3xl rounded-bl-none p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-black font-medium">{statusData.text}</span>
            {statusData.cta && (
              <Link to={statusData.cta.link}>
                <Button size="sm" className="bg-black text-[#F4DB66] hover:bg-black/80 ml-4">
                  {statusData.cta.text}
                </Button>
              </Link>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full bg-black/10 hover:bg-black/20 text-black"
          >
            <Bell className="w-5 h-5" />
          </Button>
        </div>
      )}

      <Card variant="dark" className="mb-6 p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full overflow-hidden bg-white/10 border border-white/20 shrink-0">
              <img
                src={campaign.brand.logo}
                alt={campaign.brand.name}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold text-white">{campaign.brand.name}</h1>
              <div className="flex items-center gap-2 text-gray-300">
                <span>
                  {Array.isArray(campaign.platform)
                    ? campaign.platform.join(', ')
                    : campaign.platform}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Main Upload Card */}
      <Card variant="dark">
        <CardHeader>
          <CardTitle className="text-white">Upload Final Video</CardTitle>
          <p className="text-gray-400 text-sm mt-2">
            Upload your complete video including the brand integration segment
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isVideoReviewApproved ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <p className="text-white font-semibold mb-2">Video Review Approval Required</p>
              <p className="text-gray-400 text-sm">
                Final video upload is only available once the video for review has been approved
              </p>
            </div>
          ) : (
            <>
              {/* File Upload */}
              {!activeFinalSubmission && (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-white/40 transition-colors">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="final-video-upload"
                      disabled={uploading}
                    />
                    <label htmlFor="final-video-upload" className="cursor-pointer">
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      {selectedFile ? (
                        <div>
                          <p className="text-white font-semibold">{selectedFile.name}</p>
                          <p className="text-gray-400 text-sm mt-1">
                            {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-white font-semibold">Choose your final video</p>
                          <p className="text-gray-400 text-sm mt-1">or drag and drop it here</p>
                        </div>
                      )}
                    </label>
                  </div>

                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                    variant="lime"
                    className="w-full font-medium"
                    size="lg"
                  >
                    {uploading ? 'Uploading...' : 'Upload Final Video'}
                  </Button>
                </div>
              )}

              {/* Post Configuration - show if there's a pending/approved submission */}
              {activeFinalSubmission && (
                <div className="space-y-6">
                  <div className="text-center py-4 bg-white/5 rounded-lg border border-white/10">
                    <CheckCircle className="h-12 w-12 text-[#C0FD72] mx-auto mb-4" />
                    <p className="text-white font-semibold">Final Video Submitted</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Status: {activeFinalSubmission.status}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default UploadFinalVideo
